import {Injector} from "@angular/core"
import {RenderingService} from "@app/common/services/rendering/rendering.service"
import {completedRenderPasses, createLayerRenderJob, JobData} from "@app/template-editor/helpers/template-jobs"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import {compareScenes, findLayerPlanContext, findLayerPlanLayer, LayerPlan, LayerPlanContext, LayerPlanLayer, Parameters} from "@cm/template-nodes"
import {JobNodes} from "@cm/job-nodes"
import {ImageProcessingNodes, PostProcessingInputData, PostProcessingSettings} from "@cm/image-processing-nodes"
import {layerStackImages} from "@cm/image-processing/operations/layer-stack"

export type LayerSelection = {context: LayerPlanContext; layer: LayerPlanLayer}

export function isInStack(layer: LayerPlanLayer, configuration: Readonly<Record<string, unknown>>) {
    return !layer.group || configuration[layer.group.key] === layer.variant!.id
}

export function layerLabel(layer: LayerPlanLayer) {
    return layer.group ? `${layer.group.name}: ${layer.variant!.name}` : "Rest"
}

export function stackLayers(context: LayerPlanContext, configuration: Readonly<Record<string, unknown>>) {
    return context.layers.filter((layer) => isInStack(layer, configuration))
}

export function activeSolo(plan: LayerPlan, solo: LayerSelection | undefined, configuration: Readonly<Record<string, unknown>>) {
    if (!solo || findLayerPlanContext(plan, configuration)?.key !== solo.context.key) return undefined
    return isInStack(solo.layer, configuration) ? solo : undefined
}

async function compiledScene(sceneManagerService: SceneManagerService, parameters: Record<string, string>) {
    sceneManagerService.$instanceParameters.set(new Parameters({parameters}))
    sceneManagerService.compileTemplate()
    await sceneManagerService.syncSolved(true)
    return sceneManagerService.$scene()
}

function layerParameters(context: LayerPlanContext, layer: LayerPlanLayer) {
    return layer.group ? {...context.assignment, [layer.group.key]: layer.variant!.id} : context.assignment
}

// Slot keys are `<mesh id>#<slot>`, and mesh ids are only valid in the scene manager that compiled them.
// A group's slots are collected over all its options, because the option the base already shows changes nothing.
export async function layerSlots(sceneManagerService: SceneManagerService, context: LayerPlanContext, groupKeys: readonly string[]) {
    const base = await compiledScene(sceneManagerService, context.assignment)
    const slotsByGroup = new Map<string, Set<string>>()
    for (const layer of context.layers) {
        if (!layer.group || !groupKeys.includes(layer.group.key)) continue
        const slots = slotsByGroup.get(layer.group.key) ?? new Set<string>()
        for (const slot of compareScenes(base, await compiledScene(sceneManagerService, layerParameters(context, layer))).materialSlots) slots.add(slot)
        slotsByGroup.set(layer.group.key, slots)
    }
    return {base, slotsByGroup}
}

export async function submitLayerRenderJobs(options: {
    injector: Injector
    renderingService: RenderingService
    sceneManagerService: SceneManagerService
    templateRevisionId: string
    organizationLegacyId: number
    queueGroup?: string
    context: LayerPlanContext
    layers: readonly LayerPlanLayer[]
    onSubmitted: (layer: LayerPlanLayer, job: JobData) => void
    onError: (layer: LayerPlanLayer, error: unknown) => void
    isCancelled?: () => boolean
}) {
    const {sceneManagerService, context, layers} = options
    const needed = layers.some((layer) => !layer.group)
        ? context.layers.flatMap((layer) => (layer.group ? [layer.group.key] : []))
        : layers.map((layer) => layer.group!.key)
    const {slotsByGroup} = await layerSlots(sceneManagerService, context, needed)
    const layered = new Set([...slotsByGroup.values()].flatMap((slots) => [...slots]))

    let submitted = 0
    let failed = 0
    for (const layer of layers) {
        if (options.isCancelled?.()) break
        try {
            const own = layer.group ? slotsByGroup.get(layer.group.key)! : undefined
            const holdout = (meshId: string, slot: number) => (own ? !own.has(`${meshId}#${slot}`) : layered.has(`${meshId}#${slot}`))
            const scene = await compiledScene(sceneManagerService, layerParameters(context, layer))
            // Shadows on the ground belong to the scene, not to a part, so only the rest layer renders them.
            const shadowCatcherPass = !layer.group
            const [, job] = await createLayerRenderJob(
                options.injector,
                options.renderingService,
                options.templateRevisionId,
                layer.key,
                scene,
                holdout,
                shadowCatcherPass,
                options.organizationLegacyId,
                options.queueGroup,
            )
            options.onSubmitted(layer, job)
            submitted++
        } catch (error) {
            options.onError(layer, error)
            failed++
        }
    }
    return {submitted, failed}
}

// Each layer is premultiplied and cut out where the others are visible, so their sum is the full render. Held-out
// surfaces still cast shadows, so the rest layer's shadow passes are complete; its cryptomatte misses every layered part.
export function sumLayers(rest: PostProcessingInputData, layers: readonly PostProcessingInputData[]): PostProcessingInputData {
    return {
        combinedPass: layers.reduce<PostProcessingInputData["combinedPass"]>(
            (sum, layer) => ({type: "math", firstInput: sum, secondInput: layer.combinedPass, operation: "+"}),
            rest.combinedPass,
        ),
        shadowCatcherPass: rest.shadowCatcherPass,
        shadowMaskPass: rest.shadowMaskPass,
    }
}

export type LayerExportFile = {name: string; extension: string; content: JobNodes.DataObjectReference | ImageProcessingNodes.Encode}
export type LayerExportFolder = {name: string; files: LayerExportFile[]}

export type ExportableContext = {context: LayerPlanContext; rest: JobData; groups: {name: string; options: {layer: LayerPlanLayer; job: JobData}[]}[]}

function completed(job: JobData) {
    try {
        completedRenderPasses(job)
        return true
    } catch {
        return false
    }
}

function contextFolderName(context: LayerPlanContext) {
    return context.parts.map((part) => `${part.group} ${part.variant}`).join(", ") || "All configurations"
}

// Images need the coverage of every group, so a context is exported only with its rest layer and one finished option per group.
export function exportableContexts(plan: LayerPlan, jobs: readonly {key: string; job: JobData}[]) {
    const jobsByKey = new Map(jobs.map(({key, job}) => [key, job]))
    const contexts = new Map<string, LayerPlanContext>()
    for (const {key} of jobs) {
        const found = findLayerPlanLayer(plan, key)
        if (found) contexts.set(found.context.key, found.context)
    }
    const exportable: ExportableContext[] = []
    let skipped = 0
    for (const context of contexts.values()) {
        const rest = context.layers.find((layer) => !layer.group)
        const restJob = rest && jobsByKey.get(rest.key)
        const groups = new Map<string, {name: string; options: {layer: LayerPlanLayer; job: JobData}[]}>()
        for (const layer of context.layers) {
            if (!layer.group) continue
            const group = groups.get(layer.group.key) ?? {name: layer.group.name, options: []}
            groups.set(layer.group.key, group)
            const job = jobsByKey.get(layer.key)
            if (job && completed(job)) group.options.push({layer, job})
        }
        if (!restJob || !completed(restJob) || [...groups.values()].some((group) => group.options.length === 0)) {
            skipped++
            continue
        }
        exportable.push({context, rest: restJob, groups: [...groups.values()]})
    }
    return {exportable, skipped}
}

// The shadow catcher pass multiplies onto the assembled layers, and only the rest layer renders it.
export function layerExrFolders(exportable: readonly ExportableContext[]) {
    return exportable.map(({context, rest, groups}): LayerExportFolder => {
        const restPasses = completedRenderPasses(rest)
        const files: LayerExportFile[] = [{name: "Rest", extension: "exr", content: restPasses.Combined}]
        if (restPasses.ShadowCatcher) files.push({name: "Shadow", extension: "exr", content: restPasses.ShadowCatcher})
        for (const group of groups) {
            for (const {layer, job} of group.options) {
                files.push({name: `${group.name} - ${layer.variant!.name}`, extension: "exr", content: completedRenderPasses(job).Combined})
            }
        }
        return {name: contextFolderName(context), files}
    })
}

export function layerImageFileCount(exportable: readonly ExportableContext[]) {
    return exportable.reduce((count, {groups}) => count + 1 + groups.reduce((options, group) => options + group.options.length, 0), 0)
}

function layerImageFiles(
    context: ExportableContext,
    inputs: Map<JobData, PostProcessingInputData>,
    settings: PostProcessingSettings,
    mediaType: ImageProcessingNodes.Encode["mediaType"],
) {
    const encode = (image: ImageProcessingNodes.ImageNode): ImageProcessingNodes.Encode => ({
        type: "encode",
        mediaType,
        input: {type: "convert", input: image, channelLayout: "RGBA", dataType: "uint8", sRGB: true},
        options: {keepAlpha: true},
    })
    const {rest, layers} = layerStackImages(
        inputs.get(context.rest)!,
        context.groups.map((group) => ({options: group.options.map(({layer, job}) => ({option: layer, input: inputs.get(job)!}))})),
        settings,
    )
    return [
        {name: "00 Rest", graph: encode(rest)},
        ...layers.flatMap((options, index) =>
            options.map(({option, image}) => ({
                name: `${String(index + 1).padStart(2, "0")} ${option.group!.name} - ${option.variant!.name}`,
                graph: encode(image),
            })),
        ),
    ]
}

export function layerImageFolders(
    exportable: readonly ExportableContext[],
    settingsOf: (context: LayerPlanContext) => Promise<PostProcessingSettings>,
    mediaType: ImageProcessingNodes.Encode["mediaType"],
    extension: string,
    loadInput: (job: JobData) => Promise<PostProcessingInputData>,
) {
    return Promise.all(
        exportable.map(async (context): Promise<LayerExportFolder> => {
            const contextJobs = [context.rest, ...context.groups.flatMap((group) => group.options.map(({job}) => job))]
            const [inputs, settings] = await Promise.all([
                Promise.all(contextJobs.map(async (job) => [job, await loadInput(job)] as const)).then((entries) => new Map(entries)),
                settingsOf(context.context),
            ])
            const files = layerImageFiles(context, inputs, settings, mediaType).map(({name, graph}): LayerExportFile => ({name, extension, content: graph}))
            return {name: contextFolderName(context.context), files}
        }),
    )
}
